import {
  Target,
  type StateMachineConfig,
  type BaseState,
  type BaseEvent,
  type BaseContext,
  type BaseEventLookup,
  type Transition,
  type Machine,
  type TransitionListener,
  type StateConfig,
  type EventListener,
  type StateCommand,
  type Effect,
  type Effects,
  type AsyncStateConfig,
  type AsyncResolvedEvent,
  type AsyncRejectedEvent,
  TransitionType,
} from './state-machine-types';

function getTransitionType<Event extends BaseEvent>(
  event: Event | AsyncResolvedEvent<any> | AsyncRejectedEvent,
): TransitionType {
  if (event.type === 'async-resolved') {
    return TransitionType.AsyncResolved;
  }
  if (event.type === 'async-rejected') {
    return TransitionType.AsyncRejected;
  }
  return TransitionType.Normal;
}

/**
 * Create an instance of a state machine.
 * @param config State machine configuration with candidate states and transitions.
 * @param triggerChange An optional callback indicating whether a change in context requires a state transition.
 * @returns An instance of a state machine.
 */
export function createMachine<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
>(
  config: StateMachineConfig<State, Event, EventLookup, Context>,
): Machine<State, Event, EventLookup, Context> {
  type StateNode = StateConfig<State, Event, EventLookup, Context>;
  type StateTree = Partial<Record<State, StateNode>>;
  type StatePathNode = {
    state: State;
    node: StateNode;
  };

  const pathLookup = new Map<State | StateCommand, StatePathNode[]>();
  const currentConfig: StateMachineConfig<State, Event, EventLookup, Context> = config;

  const transitionListeners: {
    listener: TransitionListener<Event, EventLookup, Context, State>;
    type: TransitionType;
  }[] = [];

  const eventListeners: EventListener<Event>[] = [];
  const doneListeners: VoidFunction[] = [];
  let abortController: AbortController | undefined;

  const prepare = (
    stateTree: StateTree,
    statePrefix = '',
    statePath: StatePathNode[] = [],
  ) => {
    for (const [state, branch] of Object.entries(stateTree)) {
      const path = `${statePrefix}${state}`;
      const nextTree = branch as StateNode;
      const nextPath = [...statePath, { state, node: branch }] as StatePathNode[];

      pathLookup.set(state as State, nextPath);

      if (nextTree.states) {
        prepare(nextTree.states, `${path}:`, nextPath);
      }
    }
  };

  // Pre-warm all state node paths.
  prepare(config.states);

  const getNode = (
    statePath: StatePathNode[],
    position = statePath.length,
  ): StateNode => {
    return statePath[position - 1].node;
  };

  const findState = (state: State): StatePathNode[] => {
    return pathLookup.get(state) ?? (console.log(pathLookup, config, state), []);
  };

  const findStateConfig = (
    target: State,
  ): StateConfig<State, Event, EventLookup, Context> | undefined => {
    return findState(target).at(-1)?.node;
  };

  const findTransition = (
    statePath: StatePathNode[],
    event: EventLookup[Event['type']],
  ): Transition<State, Context, EventLookup, Event['type']> | undefined => {
    function firstMatchingTransition(
      transitions: Transition<State, Context, EventLookup, Event['type']>[],
    ): Transition<State, Context, EventLookup, Event['type']> | undefined {
      const transition = transitions.find((transition) => {
        const transitionGuards = transition.guard.every((guard) =>
          guard(currentContext, event),
        );
        if (!transitionGuards) {
          return;
        }

        if (transitionGuards && transition.state === 'done') {
          return transition;
        }

        const statePath = pathLookup.get(transition.state);
        if (!statePath) {
          return;
        }

        const stateGuards = getNode(statePath)?.guards;
        if (!stateGuards) {
          return transitionGuards ? transition : undefined;
        }

        if (stateGuards.every((g) => g())) {
          return transition;
        }
      });

      if (transition) {
        return transition;
      }
    }

    for (let idx = statePath.length - 1; idx >= 0; idx--) {
      const node = getNode(statePath, idx + 1);
      if ('transitions' in node) {
        const transitions = node.transitions[event.type as Event['type']] ?? [];

        const transition = firstMatchingTransition(transitions);

        if (transition) {
          return transition;
        }

        if (node.trap) {
          return undefined;
        }
      }
    }

    return firstMatchingTransition(
      config.globalTransitions?.[event.type as Event['type']] ?? [],
    );
  };
  const sendEvent = (event: Event, target: Target = Target.Listeners) => {
    if (target & Target.Listeners) {
      eventListeners.forEach((listener) => listener(event));
    }

    if (target & Target.StateMachine) {
      send(event as unknown as EventLookup[Event['type']]);
    }
  };

  let prevStatePath: StatePathNode[] = findState(currentConfig.initial);
  let currentStatePath: StatePathNode[] = findState(currentConfig.initial);

  const initialNode = {
    state: currentConfig.initial,
    node: currentStatePath[currentStatePath.length - 1].node,
  };

  let prevContext: Context = { ...currentConfig.context };
  let currentContext: Context = { ...currentConfig.context };

  const send = (event: EventLookup[Event['type']]) => {
    const state = getNode(currentStatePath);

    if ('passthroughs' in state) {
      // When in a passthrough state, we do not perform explicit transitions.
      // Such transitions occur once, on entry. If not satisfied, the machine
      // will remain in the passthrough state.
      return;
    }

    const transition = findTransition(currentStatePath, event);

    if (transition) {
      doTransition(event, transition);
      doPassthrough(event);
    }
  };

  const doTransition = <T extends Event['type']>(
    event: EventLookup[T] | AsyncResolvedEvent<any> | AsyncRejectedEvent,
    transition: Transition<State, Context, EventLookup, T>,
  ) => {
    prevStatePath = [...currentStatePath];

    // If we have reached a final state, we tell the consumer of the state machine,
    // and reset it to its initial state.
    if (transition.state === 'done') {
      resolveContext<T>(transition, event);
      effect(
        getNode(currentStatePath),
        'onExit',
        currentContext,
        currentContext,
        sendEvent,
      );

      doneListeners.forEach((listener) => listener());
      return;
    }

    currentStatePath = findState(transition.state);

    prevContext = { ...currentContext };
    // Once a transition has been found, update the current context in preparation
    // for executing any effects for this state transition.
    resolveContext<T>(transition, event);
    transitionListeners.forEach((listener) => {
      const transitionType = getTransitionType(event);

      if (listener.type & transitionType) {
        listener.listener(
          event as EventLookup[T],
          currentContext,
          {
            previous: previous(),
            current: state(),
          },
          transition,
        );
      }
    });

    resolveEffects();
  };

  const doPassthrough = <T extends Event['type']>(event: EventLookup[T]) => {
    const nextState = getNode(currentStatePath);
    if ('passthroughs' in nextState) {
      const passthrough = nextState.passthroughs.find((passthrough) =>
        passthrough.guard.every((guard) => guard(currentContext, event)),
      );

      if (!passthrough) {
        return;
      }

      doTransition(event, passthrough);
    }
  };

  const previous = (): State => prevStatePath[prevStatePath.length - 1].state;
  const current = (): State => currentStatePath[currentStatePath.length - 1].state;

  const state = (): State => current();
  const context = (): Context => currentContext;

  const resolveContext = <T extends Event['type']>(
    transition: Transition<State, Context, EventLookup, T>,
    event: EventLookup[T] | AsyncResolvedEvent<any> | AsyncRejectedEvent,
  ) => {
    transition.context.forEach((contextUpdater) => {
      currentContext = contextUpdater(currentContext, event as EventLookup[T]);
    });
  };

  const doAsyncTransition = (
    node: AsyncStateConfig<State, Event, EventLookup, Context, any>,
    event: AsyncResolvedEvent<any> | AsyncRejectedEvent,
  ) => {
    let transition: Transition<State, Context, any, any> | undefined;
    if (event.type === 'async-resolved') {
      transition = node.resolvedTransitions.find((t) =>
        t.guard.every((guard) => guard(currentContext, event)),
      );
    } else {
      transition = node.rejectedTransitions.find((t) =>
        t.guard.every((guard) => guard(currentContext, event)),
      );
    }

    if (!transition) {
      return;
    }

    // The async types used for async states are not defined as normal events
    // that can be consumed in the state machine (they are special); we need to
    // cast to any to avoid type errors.
    doTransition(event, transition as Transition<State, Context, any, any>);
  };

  const setupAsyncStateTimeout = (
    node: AsyncStateConfig<State, Event, EventLookup, Context, any>,
    signal: AbortSignal,
  ) => {
    if (node.timeout) {
      const timeout =
        typeof node.timeout === 'function' ? node.timeout(currentContext) : node.timeout;

      if (timeout <= 0) {
        return;
      }

      const timeoutId = setTimeout(() => {
        if (!signal.aborted) {
          doAsyncTransition(node, {
            type: 'async-rejected',
            error: new Error('Async action timed out'),
            reason: 'timeout',
          });
          abortController?.abort();
        }
      }, timeout);

      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timeoutId);
        },
        {
          once: true,
        },
      );
    }
  };

  const processAsyncState = (node: StateNode) => {
    abortController?.abort();

    if ('asyncAction' in node) {
      abortController = new AbortController();
      const currentSignal = abortController.signal;

      setupAsyncStateTimeout(node, currentSignal);

      node
        .asyncAction(currentContext, currentSignal)
        .then((result) => {
          if (currentSignal.aborted) {
            return;
          }

          doAsyncTransition(node, {
            type: 'async-resolved',
            result,
          });
        })
        .catch((error) => {
          if (currentSignal.aborted) {
            return;
          }

          doAsyncTransition(node, {
            type: 'async-rejected',
            error,
            reason: 'error',
          });
        });

      return;
    }
  };

  const resolveEffects = () => {
    let prev = 0;
    let curr = 0;
    let overlapUntil = 0;

    while (prev < prevStatePath.length && curr < currentStatePath.length) {
      if (prevStatePath[prev].state === currentStatePath[curr].state) {
        overlapUntil++;
      }
      prev++;
      curr++;
    }

    for (prev = overlapUntil; prev < prevStatePath.length; prev++) {
      effect(
        getNode(prevStatePath, prev + 1),
        'onExit',
        prevContext,
        currentContext,
        sendEvent,
      );
    }

    const node = getNode(currentStatePath, currentStatePath.length);
    processAsyncState(node);

    for (curr = overlapUntil; curr < currentStatePath.length; curr++) {
      effect(getNode(currentStatePath, curr + 1), 'onEnter', currentContext, sendEvent);
    }

    if (previous() === current()) {
      effect(
        getNode(currentStatePath),
        'onReEnter',
        prevContext,
        currentContext,
        sendEvent,
      );
    }
  };

  const execute = () => {
    effect(getNode(currentStatePath), 'onExecute', currentContext, sendEvent);
  };

  const effect = (
    effectState: StateConfig<State, Event, EventLookup, Context>,
    effectKey: Effect,
    ...args: Parameters<Effects<Context, Event>[Effect][number]>
  ) => {
    const effects = effectState.effects;
    if (!effects[effectKey] || effects[effectKey].length === 0) {
      return;
    }

    // TODO(CNS-17473): tighten typings; slightly complex as we need to pass in
    // the correct number of arguments to the effect function, and each have
    // different signatures.
    effects[effectKey].forEach((fn) => {
      try {
        fn(args[0] as any, args[1] as any, args[2] as any);
      } catch (error) {
        console.error(error);
      }
    });
  };

  const from = (initial: State, context?: Context) => {
    const nextConfig: StateMachineConfig<State, Event, EventLookup, Context> = {
      ...config,
      initial,
    };

    if (context) {
      nextConfig.context = context;
    }

    const machine = createMachine(nextConfig);

    transitionListeners.forEach((transitionListener) => {
      machine.onTransition(transitionListener.listener);
    });
    doneListeners.forEach((doneListener) => {
      machine.onDone(doneListener);
    });

    return machine;
  };

  const reset = () => {
    abortController?.abort();
    abortController = undefined;

    prevStatePath = [{ ...initialNode }];
    currentStatePath = [{ ...initialNode }];

    prevContext = { ...currentConfig.context };
    currentContext = { ...currentConfig.context };
  };

  const destroy = () => {
    abortController?.abort();
    abortController = undefined;

    prevStatePath.length = 0;
    currentStatePath.length = 0;

    transitionListeners.length = 0;
    eventListeners.length = 0;
    doneListeners.length = 0;

    pathLookup.clear();
  };

  const onTransition = (
    listener: TransitionListener<Event, EventLookup, Context, State>,
    type: TransitionType = TransitionType.Normal,
  ) => {
    transitionListeners.push({ listener, type });
  };

  const onEvent = (listener: EventListener<Event>) => {
    eventListeners.push(listener);
  };

  const onDone = (listener: VoidFunction) => {
    doneListeners.push(listener);
  };

  return {
    send,
    state,
    context,
    execute,
    reset,
    destroy,
    onTransition,
    onEvent,
    onDone,
    from,
    config: () => ({
      initial: config.initial,
      states: { ...config.states },
      globalTransitions: { ...config.globalTransitions },
      context: { ...config.context },
    }),
    lookup: findStateConfig,
  };
}
